/**
 * EditImageStage — the `<div className="viz-block">` content from Edit.jsx.
 *
 * Tank Redesign port (PR 9.1 cont.): wraps the image canvas + overlays +
 * crop frame + crop controls so the Edit page becomes a thin orchestrator.
 * Mechanical extraction; same JSX, same prop semantics, same refs.
 *
 * The face-overlay coordinate logic (with the `loaded_from_output`
 * plumbing fixed in `1355096`) lives in FaceOverlay itself; this stage
 * only owns the wrapping containers.
 *
 * Refs (forwarded from Edit.jsx so wasm-vips, image bounds, and face
 * container math keep working):
 *   - imgRef                viz-frame
 *   - imgWrapperRef         img-wrapper (used for ResizeObserver wrapperDims)
 *   - actualImgRef          ImageDisplay's <img> for natural-dims capture
 *   - facesContainerRef     face overlay parent (used by resolveClickedFace)
 */

import { forwardRef, useLayoutEffect } from 'react';

import ImageDisplay from '../ImageDisplay';
import CropFrame from '../CropFrame';
import CropMask from '../../../../shared/components/CropMask';
import {
  FaceOverlay as _FaceOverlay,
  VizOverlay,
} from '../../../../components/edit';
import FaceOverlayLayer from './FaceOverlayLayer';

void _FaceOverlay; // imported transitively via FaceOverlayLayer

const EditImageStage = forwardRef(function EditImageStage(
  {
    // Refs
    imgRef,
    imgWrapperRef,
    actualImgRef,
    facesContainerRef,
    // ImageDisplay state
    vipsProcessedUrl,
    currentVizUrl,
    currentEditedUrl,
    hasAdjustments,
    vipsProcessing,
    vipsReady,
    showVizImage,
    showOriginal,
    developSourceUrl,
    pendingCrop,
    cropBox,
    metaDoc,
    wrapperDims,
    onLayoutChange,
    onImageLoad,
    onImageError,
    imageLoading,
    // Image bounds
    imgBounds,
    cropFrameBounds,
    // Crop frame state
    cropMode,
    cropFrameProps,
    zoom,
    panX,
    panY,
    cropRotation,
    onStartDrag,
    onWheel,
    onZoomIn,
    onZoomOut,
    onHandleDrag,
    onRotationDrag,
    // Crop mask + viz overlay
    showCropMask,
    maskCrop,
    vizLayersPreset,
    vizMetadata,
    vizLayers,
    // Faces
    currentFaces,
    selectedFace,
    hoverFace,
    hoveredPersonId,
    showPersonModal,
    imgDims,
    vizNaturalDims,
    resolveClickedFace,
    onSelectFace,
    onUnassignFace,
    onIgnoreFace,
    onUnignoreFace,
    onRemoveSource,
    onHoverFace,
    onOpenPersonModal,
    // Stats overlay
    selectedPhoto,
  },
  /* ref */ _ref,
) {
  // Hide face overlays when this match has tagging disabled (GDPR/non-public).
  // Such photos have no faces anyway; this also covers the field face-crop edge.
  const showFaces = selectedPhoto?.processing_plan?.tag !== false;
  const sharedFaceLayerProps = {
    faces: currentFaces,
    selectedFace,
    hoverFace,
    hoveredPersonId,
    showPersonModal,
    imgDims,
    cropBox,
    imgBounds,
    vizNaturalDims,
    resolveClickedFace,
    containerRef: facesContainerRef,
    onSelect: onSelectFace,
    onUnassign: onUnassignFace,
    onIgnore: onIgnoreFace,
    onUnignore: onUnignoreFace,
    onRemoveSource,
    onHoverFace,
    onOpenPersonModal,
  };

  // Size .img-wrapper to the photo's contain-fit dimensions (plus a
  // 12 px border). The wrapper IS the positioning context for every
  // overlay (faces, viz, crop frame), so making it match the photo
  // bounds keeps overlays aligned while letting the white frame hug
  // the bitmap exactly. JS-driven because CSS aspect-ratio alone
  // doesn't drive sizing in a flex parent without an explicit dim.
  useLayoutEffect(() => {
    const frame = imgRef?.current;
    const wrap = imgWrapperRef?.current;
    if (!frame || !wrap) return undefined;
    const nW = imgBounds?.nW;
    const nH = imgBounds?.nH;
    const update = () => {
      if (!nW || !nH) {
        wrap.style.width = '';
        wrap.style.height = '';
        return;
      }
      const BORDER = 12;
      const cw = frame.clientWidth;
      const ch = frame.clientHeight;
      const innerW = Math.max(0, cw - 2 * BORDER);
      const innerH = Math.max(0, ch - 2 * BORDER);
      if (innerW <= 0 || innerH <= 0) {
        wrap.style.width = '';
        wrap.style.height = '';
        return;
      }
      const photoAr = nW / nH;
      const innerAr = innerW / innerH;
      let pw;
      let ph;
      if (innerAr > photoAr) {
        ph = innerH;
        pw = ph * photoAr;
      } else {
        pw = innerW;
        ph = pw / photoAr;
      }
      wrap.style.width = `${Math.round(pw + 2 * BORDER)}px`;
      wrap.style.height = `${Math.round(ph + 2 * BORDER)}px`;
      // After the wrapper resizes, nudge the underlying <img> so
      // updateImgBounds (subscribed to img 'load' in Edit.jsx) re-fires
      // against the new wrapper dims. Otherwise on first paint imgBounds
      // is stamped against the fallback (full-canvas) wrapper and every
      // overlay lands offset until the user navigates to another photo.
      const img = wrap.querySelector('img');
      if (img && img.complete) {
        try { img.dispatchEvent(new Event('load')); } catch { /* noop */ }
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(frame);
    return () => ro.disconnect();
  }, [imgBounds?.nW, imgBounds?.nH, imgRef, imgWrapperRef]);

  return (
    <div className="viz-block">
      <div className="viz-frame tall" ref={imgRef}>
        <div
          className="img-wrapper"
          ref={imgWrapperRef}
        >
          {/* ImageDisplay hidden during crop mode but kept mounted for imgBounds */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              ...(cropMode ? { opacity: 0, pointerEvents: 'none' } : {}),
            }}
          >
            <ImageDisplay
              ref={actualImgRef}
              vipsProcessedUrl={vipsProcessedUrl}
              currentVizUrl={currentVizUrl}
              currentEditedUrl={currentEditedUrl}
              hasAdjustments={hasAdjustments}
              vipsProcessing={vipsProcessing}
              vipsReady={vipsReady}
              showVizImage={showVizImage}
              cropMode={cropMode}
              showOriginal={showOriginal}
              originalUrl={developSourceUrl}
              pendingCrop={pendingCrop}
              cropBox={cropBox}
              metaDoc={metaDoc}
              containerDims={wrapperDims}
              onLayoutChange={onLayoutChange}
              onLoad={onImageLoad}
              onError={onImageError}
            />
          </div>

          {imageLoading && (
            <div className="image-loading-overlay">
              <div className="loading-spinner" />
            </div>
          )}

          {/* Overlay positioned to match actual rendered image bounds */}
          <div
            className="overlay"
            style={
              imgBounds
                ? {
                    position: 'absolute',
                    left: `${imgBounds.x}px`,
                    top: `${imgBounds.y}px`,
                    right: 'auto',
                    bottom: 'auto',
                    width: `${imgBounds.w}px`,
                    height: `${imgBounds.h}px`,
                  }
                : { position: 'absolute', inset: 0 }
            }
          >
            {cropFrameProps && (
              <CropFrame
                vizUrl={cropFrameProps.vizUrl}
                frameW={cropFrameProps.frameW}
                frameH={cropFrameProps.frameH}
                baseScale={cropFrameProps.baseScale}
                zoom={zoom}
                panX={panX}
                panY={panY}
                cropRotation={cropRotation}
                imgNaturalW={cropFrameProps.nW}
                imgNaturalH={cropFrameProps.nH}
                sourceW={cropFrameProps.sourceW}
                sourceH={cropFrameProps.sourceH}
                onMouseDown={(e) => onStartDrag('move', e)}
                onWheel={onWheel}
                onZoomIn={onZoomIn}
                onZoomOut={onZoomOut}
                onHandleDrag={onHandleDrag}
                onRotationDrag={onRotationDrag}
              />
            )}

            {showVizImage && showCropMask && !cropMode && maskCrop && (
              <CropMask
                cropBox={maskCrop}
                rotation={maskCrop.rotation || 0}
                maskId="editCropMask"
              />
            )}

            {showVizImage && !cropMode && vizLayersPreset !== 'off' && imgBounds && vizMetadata && (
              <VizOverlay metadata={vizMetadata} imgBounds={imgBounds} layers={vizLayers} />
            )}

            {/* Face overlays — crop-mode transforms with zoom/pan; otherwise static */}
            {cropMode && cropFrameProps ? (
              <div
                className="crop-mode-faces"
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: `${cropFrameProps.frameW}px`,
                  height: `${cropFrameProps.frameH}px`,
                  transform: 'translate(-50%, -50%)',
                  overflow: 'hidden',
                  pointerEvents: 'none',
                  zIndex: 11,
                }}
              >
                <div
                  ref={facesContainerRef}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: `${cropFrameProps.nW * cropFrameProps.baseScale * zoom}px`,
                    height: `${cropFrameProps.nH * cropFrameProps.baseScale * zoom}px`,
                    transform: `translate(-50%, -50%) translate(${panX}px, ${panY}px) rotate(${cropRotation}deg)`,
                    transformOrigin: 'center center',
                  }}
                >
                  {showFaces && <FaceOverlayLayer {...sharedFaceLayerProps} viewMode="viz" />}
                </div>
              </div>
            ) : (
              <div
                ref={facesContainerRef}
                style={
                  cropFrameBounds
                    ? {
                        position: 'absolute',
                        left: `${cropFrameBounds.x}px`,
                        top: `${cropFrameBounds.y}px`,
                        width: `${cropFrameBounds.w}px`,
                        height: `${cropFrameBounds.h}px`,
                      }
                    : { position: 'absolute', inset: 0 }
                }
              >
                {showFaces && (
                  <FaceOverlayLayer
                    {...sharedFaceLayerProps}
                    viewMode={showVizImage ? 'viz' : 'edit'}
                  />
                )}
              </div>
            )}

          </div>
        </div>
      </div>

    </div>
  );
});

export default EditImageStage;
